import React, {memo, FC, useState, useEffect, useCallback, useRef} from "react";
import {FormikProps} from "formik";
import {Moment} from "moment";
import {Alert, Button, message, TableProps, Modal} from "antd";
import classNames from "classnames";
import {
    ArrayDataOptions,
    CourseLesson,
    CourseLessonBooking,
    GetBookingsByCourseIdWithCourseLessonBookingsQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    Maybe,
    UpdateCourseAndBookingsInput,
    UpdateCourseAndBookingsResult,
    useUpdateCourseAndBookingsMutation,
} from "../../../generated/graphql";
import LessonTableInteractive from "./LessonTableInteractive";
import SummarySection from "../../../views/SummarySection/SummarySection";
import i18n from "../../../services/i18n";
import {processFormData} from "./helper";
import css from "./LessonBookings.less";
import Notes from "./Notes";
import PaymentsInteractive from "./PaymentLists/PaymentsInteractive";
import LoadingContent from "../../../views/LoadingContent/LoadingContent";
import {LessonBookingCourseLessonTableData} from "./LessonTablePassive";
import {isBookingSettled} from "../../../helpers/bookingDetailsRouting";

export type PreviewBookingType = UpdateCourseAndBookingsResult["bookings"][0];
export type LessonOfTheCourseType = UpdateCourseAndBookingsInput["lessons"];

type PreviewBookingProps = {
    previewCurrentBooking: PreviewBookingType | undefined;
    setPreviewCurrentBooking: (previewCurrentBooking: PreviewBookingType) => void;
    booking: GetBookingsByCourseIdWithCourseLessonBookingsQuery["bookingsByCourseIdWithCourseLessonBookings"][0];
    formikProps: FormikProps<UpdateCourseAndBookingsInput>;
    getHolidayInfo: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
    options?: Maybe<ArrayDataOptions>;
    setOptions: (options: ArrayDataOptions) => void;
    pagination?: TableProps<LessonBookingCourseLessonTableData>["pagination"];
    holidays: Array<GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number]>;
    changedPaymentIds?: {changedIds: Set<string>; addedIds: Set<string>; removedIds: Set<string>};
    isTotalPriceChanged?: boolean;
};

const PreviewBooking: FC<PreviewBookingProps> = ({
    booking,
    formikProps,
    getHolidayInfo,
    options,
    setOptions,
    pagination,
    holidays,
    previewCurrentBooking,
    setPreviewCurrentBooking,
    changedPaymentIds,
    isTotalPriceChanged,
}) => {
    const {values} = formikProps;
    const [preview, setPreview] = useState<UpdateCourseAndBookingsResult | undefined>(undefined);
    const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
    // const [previewCurrentBooking, setPreviewCurrentBooking] = useState<PreviewBookingType | undefined>(undefined);
    const [editBookedLessons, setEditBookedLessons] = useState<boolean>(false);
    const courseId = values.id;
    const currentBookingId = booking.id;

    // Refs für Debouncing und Request-Tracking (verhindert Race Conditions)
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const requestCounterRef = useRef<number>(0);
    const DEBOUNCE_DELAY_MS = 300;

    useEffect(() => {
        const previewBooking = preview?.bookings.find((booking) => booking.id === currentBookingId);

        setPreviewCurrentBooking(previewBooking as PreviewBookingType);
    }, [preview, currentBookingId]);

    const [updateCourseAndBookings, {loading: mutationLoading, error: mutationError}] = useUpdateCourseAndBookingsMutation();

    const updatePreview = useCallback(
        async ({skipUpdatedBookingsData}: {skipUpdatedBookingsData?: boolean}) => {
            // Inkrementiere Request-Counter und merke die aktuelle Request-ID
            requestCounterRef.current += 1;
            const thisRequestId = requestCounterRef.current;

            setLoadingPreview(true);
            const currentBookingHasChanged = values.updatedBookingsData?.find((booking) => booking.id === currentBookingId);

            if (!courseId) {
                message.error("Die courseId fehlt!!!");
                throw new Error("Course ID is required");
            }
            // Rev 4 HIGH-1 (Codex Cycle 3): Dritter Parameter `values` MUSS uebergeben werden.
            // values traegt nach prepareInitialValues' `...initialCourseValues`-Spread bereits
            // coursePrice (Singular, von GetCourseByIdQuery) bzw. coursePrices (Plural, von EditCourseInput).
            // Helper.tsx:processFormData nutzt jetzt den `coursePrice ?? coursePrices`-Fallback (Task A.5 Teil 2).
            // Ohne diesen Parameter faellt includedEntranceFee im dryRun-Preview faelschlich auf null —
            // betraf BEIDE Aufrufer (CourseEdit-Modify-Flow + Wrapper).
            const {editCourseData} = processFormData(values, courseId, values);
            const updatedBookingsData = skipUpdatedBookingsData ? undefined : values.updatedBookingsData;

            // filter updatedBookingsData to only include current booking
            const currentBooking = values.updatedBookingsData?.find((booking) => booking.id === currentBookingId);
            const updatedBookingsDataOnlyCurrentBooking = currentBooking ? [currentBooking] : values.updatedBookingsData;

            try {
                const result = await updateCourseAndBookings({
                    variables: {
                        dryRun: true,
                        updateCourseAndBookingsInput: {
                            ...editCourseData,
                            courseId,
                            updatedBookingsData: currentBookingHasChanged ? updatedBookingsDataOnlyCurrentBooking : updatedBookingsData,
                        },
                    },
                    // fetchPolicy: "no-cache",
                    // awaitRefetchQueries: true,
                });

                const preview = result.data?.updateCourseAndBookings;

                // NUR die Response anwenden, wenn dies der neueste Request ist
                // (verhindert dass ältere Responses neuere überschreiben)
                if (thisRequestId === requestCounterRef.current) {
                    if (preview) {
                        setPreview(preview as UpdateCourseAndBookingsResult);
                    }
                }
            } catch (error) {
                // Nur Fehler zeigen wenn dies der neueste Request ist
                if (thisRequestId !== requestCounterRef.current) {
                    return;
                }

                console.log("error updating course", error);

                // Extrahiere Fehlermeldung aus GraphQL-Fehler
                const errorMessage = (error as any)?.graphQLErrors?.[0]?.message || (error as any)?.message || "Unbekannter Fehler";

                // Spezifische Fehlerbehandlung mit Modal für kritische Fehler
                if (errorMessage.includes("transaction is aborted") || errorMessage.includes("serialization conflict") || errorMessage.includes("duplicate key")) {
                    Modal.error({
                        title: "⚠️ Vorschau konnte nicht aktualisiert werden",
                        content: (
                            <div>
                                <p>
                                    <strong>Fehler:</strong> {errorMessage}
                                </p>
                                <p style={{marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}>{"Die Vorschau konnte nicht aktualisiert werden. Dies kann auftreten, wenn:"}</p>
                                <ul style={{marginTop: "0.5rem", fontSize: "0.9rem"}}>
                                    <li>{"Mehrere Updates gleichzeitig durchgeführt werden"}</li>
                                    <li>{"Die Datenbankverbindung unterbrochen wurde"}</li>
                                    <li>{"Neue Lektionen mit den bestehenden Buchungen in Konflikt geraten"}</li>
                                </ul>
                                <p style={{marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}>{"Bitte versuche es in wenigen Sekunden erneut."}</p>
                            </div>
                        ),
                        okText: "Verstanden",
                        width: 500,
                    });
                } else if (errorMessage.includes("maximale Anzahl") || errorMessage.includes("Keine Kursstunden") || errorMessage.includes("Buchungen in Konflikt")) {
                    // Business-Logic Fehler zeigen auch ein Modal
                    Modal.error({
                        title: "❌ Validierungsfehler",
                        content: (
                            <div>
                                <p>{errorMessage}</p>
                                <p style={{marginTop: "1rem", fontSize: "0.9rem", color: "#666"}}>Bitte überprüfe deine Eingaben und versuche es erneut.</p>
                            </div>
                        ),
                        okText: "Verstanden",
                        width: 500,
                    });
                } else {
                    message.error("Kurs konnte nicht aktualisiert werden");
                }
            } finally {
                // Loading nur beenden wenn dies der neueste Request ist
                if (thisRequestId === requestCounterRef.current) {
                    setLoadingPreview(false);
                }
            }
        },
        [courseId, values, updateCourseAndBookings, currentBookingId],
    );

    // Debounced useEffect - wartet DEBOUNCE_DELAY_MS nach der letzten Änderung
    // bevor ein Server-Request gesendet wird (verhindert Race Conditions bei schnellen Änderungen)
    useEffect(() => {
        // Lösche vorherigen Timeout wenn vorhanden
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Setze neuen Timeout
        debounceTimeoutRef.current = setTimeout(() => {
            updatePreview({skipUpdatedBookingsData: false});
        }, DEBOUNCE_DELAY_MS);

        // Cleanup: Lösche Timeout wenn Component unmountet oder Dependencies ändern
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [updatePreview, values.updatedBookingsData, values.lessons, courseId, currentBookingId]);

    const GeneratePreviewButton = ({skipUpdatedBookingsData}: {skipUpdatedBookingsData?: boolean}) => {
        return (
            <Button
                onClick={async () =>
                    updatePreview({
                        skipUpdatedBookingsData: skipUpdatedBookingsData ?? false,
                    })
                }
                size="small"
                block
            >
                {"Update Preview"}
            </Button>
        );
    };

    // if (mutationLoading) {
    //     return <div>{"Loading..."}</div>;
    // }

    if (mutationLoading) {
        return (
            <div style={{height: "70%"}}>
                <LoadingContent content="Vorausschau wird aktualisiert..." />
            </div>
        );
    }

    if (mutationError) {
        const rawMessage = mutationError.message ?? "Unbekannter Fehler";
        const isPaidRateError = rawMessage.includes("Bezahlte Rate") && rawMessage.includes("darf nicht modifiziert werden");
        const isForbiddenError = rawMessage.toLowerCase().includes("forbiddenerror");

        let title = "Vorschau konnte nicht aktualisiert werden";
        let description: React.ReactNode = rawMessage;

        if (isPaidRateError) {
            title = "Bezahlte Rate kann nicht geändert werden";
            description = (
                <div>
                    <p style={{marginBottom: "0.5rem"}}>
                        {"Bereits bezahlte Raten sind gesperrt und können nicht mehr bearbeitet, gelöscht oder durch Preisänderungen beeinflusst werden."}
                    </p>
                    <p style={{marginBottom: "0.25rem", fontSize: "0.9rem", color: "#666"}}>{"Mögliche Ursachen:"}</p>
                    <ul style={{marginBottom: "0.5rem", fontSize: "0.9rem", paddingLeft: "1.25rem"}}>
                        <li>{"Direkter Bearbeitungs- oder Löschversuch einer bezahlten Rate"}</li>
                        <li>{"Eine Preis- oder Rabatt-Änderung würde eine bezahlte Rate verändern"}</li>
                    </ul>
                    <p style={{fontSize: "0.9rem", color: "#666"}}>
                        {"Bitte verwirf die Änderung oder beschränke sie auf noch nicht bezahlte Raten."}
                    </p>
                </div>
            );
        } else if (isForbiddenError) {
            title = "Aktion nicht erlaubt";
            description = rawMessage.replace(/^.*?ForbiddenError:\s*/i, "");
        }

        return (
            <div style={{padding: "1rem"}}>
                <Alert message={title} description={description} type="error" showIcon />
                <div style={{marginTop: "0.5rem"}}>
                    <GeneratePreviewButton skipUpdatedBookingsData={true} />
                </div>
            </div>
        );
    }

    if (loadingPreview) {
        return (
            <div style={{height: "70%"}}>
                <LoadingContent content="Vorausschau wird aktualisiert..." />
            </div>
        );
    }

    if (!previewCurrentBooking) {
        return (
            <div style={{width: "100%", textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100px"}}>
                <Alert message="Für diese Buchung gibt es keine Vorschau" type="info" />
            </div>
        );
    }

    const getHighestIndexOfLessonInCourse = (lessons: Array<CourseLesson>) => {
        return lessons.reduce((max, lesson) => {
            return Math.max(max, lesson.indexOfLessonInCourse ?? 0);
        }, 0);
    };

    const lessonsOfCourse = preview?.courseLessonsPreview ?? values.lessons;
    const lessonsOfCourseWithIndexOfLessonInCourse = lessonsOfCourse.map((lesson) => {
        return {
            ...lesson,
            indexOfLessonInCourse: lesson.indexOfLessonInCourse >= 0 ? lesson.indexOfLessonInCourse : getHighestIndexOfLessonInCourse(lessonsOfCourse) + 1,
        };
    });

    const courseLessonBookingsPreview = preview?.courseLessonBookingsPreview?.find((courseLessonBookings) => {
        return courseLessonBookings.bookingId === booking.id;
    })?.courseLessonBookings as Array<CourseLessonBooking>;

    const bookingIsCanceled = previewCurrentBooking.canceled;

    const renderBookingStd = () => {
        return (
            <div className={css.bookingRoot}>
                <SummarySection
                    // className={css.summarySection}
                    title={i18n.containers.bookings.BookingSummary.sectionHeaders.courseLessons()}
                    content={
                        <>
                            {/* {!editBookedLessons && <div style={{height: "32px"}} />} Spacer */}
                            <LessonTableInteractive
                                currentBooking={booking}
                                booking={previewCurrentBooking}
                                lessonsOfCourse={lessonsOfCourseWithIndexOfLessonInCourse}
                                courseLessonBookingsPreview={courseLessonBookingsPreview}
                                formikProps={formikProps}
                                getHolidayInfo={getHolidayInfo}
                                editBookedLessons={editBookedLessons}
                                setEditBookedLessons={setEditBookedLessons}
                                pagination={pagination}
                                options={options}
                                setOptions={setOptions}
                                holidays={holidays}
                                disabled={bookingIsCanceled}
                            />
                        </>
                    }
                />
                {!editBookedLessons && (
                    <>
                        <SummarySection
                            // className={css.summarySection}
                            title={i18n.containers.bookings.BookingSummary.sectionHeaders.installmentRates()}
                            content={
                                <PaymentsInteractive
                                    booking={previewCurrentBooking}
                                    formikProps={formikProps}
                                    preview={preview}
                                    disabled={bookingIsCanceled}
                                    changedPaymentIds={changedPaymentIds}
                                    originalBooking={booking}
                                    isTotalPriceChanged={isTotalPriceChanged}
                                />
                            }
                        />
                        <SummarySection className={css.summarySection} title={i18n.containers.bookings.BookingSummary.sectionHeaders.notes()} content={<Notes booking={booking} />} />
                    </>
                )}
            </div>
        );
    };

    if (bookingIsCanceled) {
        return (
            <div className={css.canceledBooking}>
                <div className={css.disabledBooking}>{renderBookingStd()}</div>
            </div>
        );
    }

    const bookingIsSettled = isBookingSettled(previewCurrentBooking);

    if (bookingIsSettled) {
        return <div className={css.canceledBooking}>{renderBookingStd()}</div>;
    }

    return renderBookingStd();
};

export default memo(PreviewBooking);
